// FeedbackDetailsModal.tsx
import { FeedbackItem, getFeedbackById, FeedbackDetails } from 'apis/adminServices/Report&fFeedbackServices';
import React, { useState, useEffect } from 'react';

interface FeedbackDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: FeedbackItem | null;
}

const FeedbackDetailsModal: React.FC<FeedbackDetailsModalProps> = ({ isOpen, onClose, feedback }) => {
  const [details, setDetails] = useState<FeedbackDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  useEffect(() => {
    const fetchFeedbackDetails = async () => {
      if (!isOpen || !feedback?.id) return;

      setLoading(true);
      setError(null);
      try {
        const data = await getFeedbackById(feedback.id);
        setDetails(data);
      } catch (err) {
        console.error('Failed to fetch feedback details:', err);
        setError('Failed to load feedback details');
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackDetails();
  }, [isOpen, feedback]);

  // Reset image error when details change
  useEffect(() => {
    if (details?.imageUrl) {
      setImageError(false);
    }
  }, [details?.imageUrl]);

  // Close zoomed image when main modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsImageZoomed(false);
    }
  }, [isOpen]);

  // Handle ESC key for zoomed image
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isImageZoomed) {
        setIsImageZoomed(false);
      }
    };

    if (isImageZoomed) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isImageZoomed]);

  if (!isOpen || !feedback) return null;

  if (loading) {
    return (
      <div
        id="feedback-modal"
        tabIndex={-1}
        aria-hidden={!isOpen}
        className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50"
      >
        <div className="relative p-4 w-full max-w-xl">
          <div className="relative bg-white rounded-lg shadow pb-4 text-center p-6">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        id="feedback-modal"
        tabIndex={-1}
        aria-hidden={!isOpen}
        className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50"
      >
        <div className="relative p-4 w-full max-w-xl">
          <div className="relative bg-white rounded-lg shadow pb-4 text-center p-6">
            {error}
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded-md">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="feedback-modal"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-xl">
        <div className="relative bg-white rounded-lg shadow pb-4">
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">Feedback Details</p>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="flex items-center gap-4">
              <img
                src={`https://ui-avatars.com/api/?name=${details?.username || feedback.name}&size=64`}
                alt={`${details?.username || feedback.name}'s profile`}
                className="w-16 h-16 rounded-full"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                <p className="text-base font-semibold text-grease-100">{details?.username || feedback.name}</p>
                  {details?.feedbackTypeName && (
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        details.feedbackTypeName.toLowerCase() === 'compliment'
                          ? 'bg-green-100 text-green-700'
                          : details.feedbackTypeName.toLowerCase() === 'complaint'
                          ? 'bg-red-100 text-red-700'
                          : details.feedbackTypeName.toLowerCase() === 'suggestion'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {details.feedbackTypeName}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{feedback.email}</p>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-light text-gray-500">Feedback Comment</label>
              <div className="bg-blue-50 text-gray-600 text-sm rounded-md h-32 block w-full p-4 mt-2 overflow-auto">
                {details?.feedbackComment || 'No comment available'}
              </div>
            </div>
            {details?.imageUrl && (
              <div className="mt-4">
                <label className="text-xs font-light text-gray-500">Attached Image</label>
                <div className="mt-2">
                  {!imageError ? (
                    <img
                      src={details.imageUrl}
                      alt="Feedback attachment"
                      className="max-w-full max-h-64 rounded-md border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setIsImageZoomed(true)}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="bg-gray-100 border border-gray-200 rounded-md p-4 text-center">
                      <p className="text-sm text-gray-500">Failed to load image</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Image Zoom Modal */}
      {isImageZoomed && details?.imageUrl && !imageError && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setIsImageZoomed(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              type="button"
              className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition"
              onClick={() => setIsImageZoomed(false)}
              aria-label="Close zoomed image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={details.imageUrl}
              alt="Feedback attachment (zoomed)"
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDetailsModal;